import { RefBool, Ref, RefString, Collection, RefNumber } from "@tblabs/truffle";
import { Field } from "./Field";
import { GalleryItem } from "./GalleryItem";
import { Opinion } from "./Opinion";
import { PriceFactor } from "./PriceFactor";
import { ProductCategory } from "./ProductCategory";
import { ProductOption } from "./ProductOption";
import { ProductOptions } from "./ProductOptions";
import { QuantityDiscount } from "./QuantityDiscount";
import { RentPrice } from "./RentPrice";
import { RawProduct } from "./RawProduct";
import { Tab } from "./Tab";
import { DescriptionFieldConverter } from "./DescriptionFieldConverter";
import { BasketItemType } from "./BasketItemType";


export class Product
{
    public Id: string = "";
    public Visible = new RefBool();
    public Rentable = new RefBool();
    public Saleable = new RefBool();
    public Basketable = new RefBool();
    public Options = new ProductOptions();
    public AddToBasketAs = new Ref<BasketItemType>(BasketItemType.Unset);
    public CategoryId = new RefString();
    public Url = new RefString();
    public Title = new RefString();
    public Subtitle = new RefString();
    public BasketName = new RefString();
    public Tags = new RefString();
    public RentPrices = new Collection<RentPrice>();
    public PriceFactor = new Collection<PriceFactor>();
    public Discounts = new Collection<QuantityDiscount>();
    public SalePrice = new RefNumber();
    public Deposit = new RefNumber();
    public Features = new RefString();
    public Tabs = new Collection<Tab>([
        new Tab({ Label: "Opis", Icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-presentation-icon lucide-presentation"><path d="M2 3h20"/><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3"/><path d="m7 21 5-5 5 5"/></svg>` }),
        new Tab({ Label: "Szczegły", Icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-package-icon lucide-package"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><polyline points="3.29 7 12 12 20.71 7"/><path d="m7.5 4.27 9 5.15"/></svg>` }),
        new Tab({ Label: "FAQ i Opinie", Icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-microchip-icon lucide-microchip"><path d="M18 12h2"/><path d="M18 16h2"/><path d="M18 20h2"/><path d="M18 4h2"/><path d="M18 8h2"/><path d="M4 12h2"/><path d="M4 16h2"/><path d="M4 20h2"/><path d="M4 4h2"/><path d="M4 8h2"/><path d="M8 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-1.5c-.276 0-.494.227-.562.495a2 2 0 0 1-3.876 0C9.994 2.227 9.776 2 9.5 2z"/></svg>` }),
    ]);
    public Description = new Collection<Field>();
    public Images = new Collection<GalleryItem>();
    public Opinions = new Collection<Opinion>();
    public Extras = new Collection<Product>(); // TODO:
    public TutorialLink = new RefString();
    public Category: ProductCategory = ProductCategory.NotFound();
    public IsAvailable = new RefBool(true);
    public StatusLabel = new RefString("🟢 Produkt dostępny");
    public StatusColor = new RefString("#4ade80");
    public ShowDiscounts = new RefBool(true);
    public ShowExtraInfo = new RefBool(true);
    public Rating = new RefNumber(5); // per 5
    static StatusColors = {
        "Zielony": "#4ade80",
        "Czerwony": "#ef4444",
        "Żółty": "#eab308",
        "Niebieski": "#3b82f6",
        "Pomarańczowy": "#fb923c",
        "Szary": "#6b7280",
        "Biały": "#f3f4f6",
        "Czarny": "#000000"
    };

    constructor(raw?: Partial<RawProduct>)
    {
        this.Id = raw?.Id || "";
        this.Visible.value = raw?.Visible ?? true;
        this.Rentable.value = raw?.Rentable ?? false;
        this.Saleable.value = raw?.Saleable ?? false;
        this.Basketable.value = raw?.Basketable ?? false;
        this.AddToBasketAs.value = raw?.AddToBasketAs as BasketItemType || BasketItemType.Unset;
        this.Options.List.Load(...(raw?.Options?.List?.map(x => new ProductOption(x)) || []));
        this.CategoryId.value = raw?.CategoryId || "";
        this.Url.value = raw?.Url || "";
        this.Title.value = raw?.Title || "";
        this.Subtitle.value = raw?.Subtitle || "";
        this.BasketName.value = raw?.BasketName || "";
        this.Tags.value = raw?.Tags || "";
        if (raw?.RentPrices)
            this.RentPrices.Load(...raw.RentPrices.map(x => new RentPrice(x)));
        else this.RentPrices.Load(
            new RentPrice({ Days: 3, Visible: true }),
            new RentPrice({ Days: 7, Visible: true }),
            new RentPrice({ Days: 14, Visible: true }),
            new RentPrice({ Days: 30 }),
            new RentPrice({ Days: 90 }));
        if (raw?.PriceFactor)
            this.PriceFactor.Load(...raw.PriceFactor.map(x => new PriceFactor(x)));
        this.SalePrice.value = raw?.SalePrice || 0;
        this.Discounts.Load(...(raw?.Discounts?.map(x => new QuantityDiscount(x)) || []));
        this.Deposit.value = raw?.Deposit || 0;
        this.Features.value = raw?.Features || "";
        this.Tabs.Load(...(raw?.Tabs?.map(x => new Tab(x)) || []));
        this.Description.Load(...(raw?.Description?.map(x => DescriptionFieldConverter.ToClass(x)) || []));
        this.Images.Load(...(raw?.Images?.map(x => new GalleryItem(x)) || []));
        this.Opinions.Load(...(raw?.Opinions?.map(x => new Opinion(x)) || []));
        this.TutorialLink.value = raw?.TutorialLink || "";
        this.StatusLabel.value = raw?.StatusLabel || "🟢 Produkt dostępny";
        this.StatusColor.value = raw?.StatusColor ?? Product.StatusColors["Zielony"];
        this.ShowDiscounts.value = raw?.ShowDiscounts ?? true;
        this.ShowExtraInfo.value = raw?.ShowExtraInfo ?? true;
        this.Rating.value = raw?.Rating || 5;
    }
}
